import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSWRConfig } from 'swr';
import { SWRInfiniteKeyedMutator } from 'swr/infinite';
import { v4 as uuid } from 'uuid';

import {
  clearOrbitSession,
  createOrbitRequest,
  findLandedOrbitTurn,
  isFatalOrbitPollError,
  isTerminalOrbitState,
  markOrbitTurnSeen,
  ORBIT_REQUESTS_PATH,
  OrbitMutationFailure,
  OrbitMutationOutcome,
  OrbitPendingSince,
  revalidateOrbitSessionLists,
  useOrbitTurn,
  useOrbitTurnPages,
  useOrbitTurnTimeouts
} from 'api/orbit';
import {
  CARD_KEY_SEPARATOR,
  ScreenCardInView,
  ScreenFilterChip,
  ScreenFilterSignature,
  cardSetKey,
  screenFilterSummary,
  useScreenFilterSignature
} from 'containers/Intelligence/filters/screenFilterSignature';
import { useTypedSelector } from 'hooks';

import OrbitContext, { OrbitContextValue } from './OrbitContext';
import OrbitNoticeContext from './OrbitNoticeContext';
import OrbitNoticeHost from './components/OrbitNoticeHost';
import { ORBIT_SCREEN_CUSTOMER_360 } from './constants';
import { ORBIT_CONTEXT_DETECTION_ENABLED } from './constants';
import { orbitSessionSubject } from './contextLabel';
import { orbitNewInvestigationNotice } from './orbitNotices';
import { orbitQuestionKey } from './promptStarters';
import {
  clearStoredOrbitSession,
  orbitSessionStorageKey,
  readStoredOrbitSession,
  updateStoredOrbitSession,
  writeStoredOrbitSession
} from './sessionStorage';
import { OrbitEntryContext, OrbitListResponse, OrbitTurn } from './types';
import useOrbitEntryContext, { toOrbitWireContext } from './useOrbitEntryContext';
import useOrbitNoticeState from './useOrbitNoticeState';
import useOrbitWorkspaceFiling from './useOrbitWorkspaceFiling';

export interface OrbitProviderProps {
  children?: React.ReactNode;
}

/**
 * Identity of a context for dismissal bookkeeping: a later move elsewhere raises the card again.
 * The dashboard filters and the card in view are part of it because either changing is itself a
 * context change (PLA-8844 D2, user 2026-09-14), so a decision taken about one does not silence the
 * next one.
 */
const contextKey = (
  context: OrbitEntryContext,
  filterKey: string | null,
  cardsKey: string | null
): string =>
  `${context.screen ?? ''}|${context.customer_id ?? ''}|${filterKey ?? ''}|${cardsKey ?? ''}`;

/** The stored form of a visible set: the joined identity back as the keys it was built from. */
const storedCardKeys = (cardsKey: string | null): string[] | undefined =>
  cardsKey === null || cardsKey === '' ? undefined : cardsKey.split(CARD_KEY_SEPARATOR);

/**
 * The ids a screenful of cards sends as `context.metric_ids` (PLA-9791): every card the registry maps
 * to a catalogue id, de-duplicated, in reading order. A card the registry maps to nothing keeps its
 * chip but is passed over rather than sent as a hole. `metric_id` stays the first of them, the
 * topmost card, for a backend that reads only the single id.
 */
const mappedMetricIds = (cards: ScreenCardInView[]): string[] =>
  Array.from(new Set(cards.flatMap(card => (card.metricId === null ? [] : [card.metricId]))));

/** One frozen empty set, so a screen publishing no card does not churn the memos below. */
const NO_CARDS: ScreenCardInView[] = [];
/** The same, for a screen that publishes no filter chip. */
const NO_CHIPS: ScreenFilterChip[] = [];
const NO_TURN_IDS: ReadonlySet<number> = new Set();

const withTurnId = (ids: ReadonlySet<number>, id: number): ReadonlySet<number> =>
  ids.has(id) ? ids : new Set(ids).add(id);

const NO_LOST_TURNS: ReadonlyMap<number, OrbitTurn> = new Map();

/**
 * The turn a 404 leaves behind, drawn from what this tab already knows: its id, the question as
 * typed, and `failed`, which is the state the transcript draws with its own error copy and Retry.
 * Everything else is empty because nothing was ever answered.
 */
const lostTurn = (id: number, sessionId: string | null, question: string): OrbitTurn => ({
  id,
  session_id: sessionId ?? '',
  state: 'failed',
  outcome: null,
  question,
  context: {},
  metric_results: [],
  answer: {},
  report_download_id: null,
  feedback: null,
  created_at: new Date().toISOString()
});

/**
 * How long the page is given to come to rest after the panel opens or closes.
 *
 * The panel is 489 px of the SAME flex row as the content (`OrbitShell`), so a toggle reflows the
 * page and the browser re-clamps its scroll position: measured on Customer Insights at 1440x900,
 * minimising the panel moved the page from scrollY 2360 to 1551 with no input from the reader, and
 * the set of cards the detector reported changed with it. For this window the comparison is held
 * and whatever the detector settles on afterwards becomes the card baseline, so the panel's own
 * width is never reported as a context change (PLA-8844, user 2026-09-14). It covers the wrapper's
 * 240 ms width transition plus `useCardInView`'s 150 ms settle.
 */
export const ORBIT_PANEL_RELAYOUT_MS = 600;

/** One question asked in another tab writes the stored session several times in a row. */
export const ORBIT_TRANSCRIPT_REFRESH_THROTTLE_MS = 1000;
/** A send whose POST got no response, so the server may or may not hold its turn. */
interface UnconfirmedOrbitSend {
  /** `sessionId` when it was sent: `null` for the opening question of a new chat. */
  fromSessionId: string | null;
  sessionId: string;
  question: string;
  /** The turns already listed when it was first sent, none of which can be the one it created. */
  knownTurnIds: number[];
}

// The turn echo carries the server-added `deploy_target`, which is not part of what the user chose.
const entryContextOf = (turn: OrbitTurn | undefined): OrbitEntryContext | null =>
  turn === undefined
    ? null
    : {
        screen: turn.context.screen,
        metric_id: turn.context.metric_id,
        metric_ids: turn.context.metric_ids,
        filters: turn.context.filters,
        date_range: turn.context.date_range,
        customer_id: turn.context.customer_id
      };

/**
 * Owns the assistant's conversation state for the whole app shell: the open flag, the persisted
 * session id, the turn in flight and its polling. Mounted once above the routes so the
 * conversation survives navigation. PLA-8844.
 */
export const OrbitProvider: React.FC<OrbitProviderProps> = ({ children }) => {
  const tenantId = useTypedSelector(state => state.global?.tenantConfig?.id);
  const userId = useTypedSelector(state => state.global?.authUser?.id);
  const storageKey = tenantId && userId ? orbitSessionStorageKey(tenantId, userId) : null;
  const routeContext = useOrbitEntryContext();
  /**
   * What the screen the user is on is currently showing: its filter selections, and every card the
   * page has on screen. Published to a module store because each screen's provider wraps only that
   * screen while this one is mounted in the app shell; `null` on every screen that publishes none.
   *
   * All of it reaches the wire (PLA-9791): the filters as `filters` and `date_range`, built from the
   * same selections as the chips, and the cards as `metric_ids`. The backend currently permits only
   * screen/metric_id/customer_id and drops the rest until that ships.
   */
  const entryFilters = useScreenFilterSignature();
  const entryFilterKey = entryFilters?.key ?? null;
  /** Every card the reader has on screen, in reading order. Empty until the page has reported a set. */
  const entryCards = entryFilters?.cards ?? NO_CARDS;
  /**
   * What the screenful sends as `context.metric_ids` (PLA-9791) and, its first, `context.metric_id`
   * (PLA-8839 §7). Catalogue ids and never titles: FR §4.3 admits only an id that exists in the
   * deployed `metrics.yml` catalogue, so a screenful the registry maps to nothing sends neither.
   * Joined for the memo below, which would otherwise see a fresh array every render.
   */
  const entryMetricIdsKey = mappedMetricIds(entryCards).join(CARD_KEY_SEPARATOR);
  /** Identity of the visible set. `null` until the screen's card tree has reported one. */
  const entryCardsKey = entryCards.length === 0 ? null : cardSetKey(entryCards);
  // Serialised so the memo below keys on the values, not on a signature rebuilt every publish.
  const entryWireFiltersKey = JSON.stringify({
    filters: entryFilters?.filters,
    dateRange: entryFilters?.dateRange
  });
  const entryContext = useMemo<OrbitEntryContext>(() => {
    if (routeContext.screen === undefined) {
      return routeContext;
    }

    const { filters, dateRange } = JSON.parse(entryWireFiltersKey) as Pick<
      ScreenFilterSignature,
      'filters' | 'dateRange'
    >;
    const metricIds = entryMetricIdsKey === '' ? [] : entryMetricIdsKey.split(CARD_KEY_SEPARATOR);

    return {
      ...routeContext,
      ...(metricIds.length === 0 ? {} : { metric_id: metricIds[0], metric_ids: metricIds }),
      ...(filters === undefined || Object.keys(filters).length === 0 ? {} : { filters }),
      ...(dateRange === undefined ? {} : { date_range: dateRange })
    };
  }, [routeContext, entryMetricIdsKey, entryWireFiltersKey]);

  /** Every chip the live screen is drawing, for the row and for the record the pin freezes. */
  const entryChips = entryFilters?.chips ?? NO_CHIPS;
  /** What the compare row and the stored record name the filters by. `''` when none is published. */
  const entryFilterLabel = screenFilterSummary(entryFilters);
  const { mutate: mutateCache } = useSWRConfig();

  const notices = useOrbitNoticeState();
  const { showNotice } = notices;

  const [open, setOpen] = useState(false);
  const [pageSurfaces, setPageSurfaces] = useState(0);
  const attachPageSurface = useCallback(() => {
    setPageSurfaces(count => count + 1);
    return () => setPageSurfaces(count => count - 1);
  }, []);
  const transcriptActive = open || pageSurfaces > 0;
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activeTurnId, setActiveTurnId] = useState<number | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  /**
   * Turns whose poll answered 404 AND which the transcript no longer lists. Without them the
   * question would leave the screen with nothing to say why, and the empty-transcript guard below
   * would read the session as ended and reset the panel to the welcome screen.
   */
  const [lostTurns, setLostTurns] = useState<ReadonlyMap<number, OrbitTurn>>(NO_LOST_TURNS);
  const [askError, setAskError] = useState<OrbitMutationFailure | null>(null);
  const [sessionStale, setSessionStale] = useState(false);
  const [draft, setDraft] = useState('');
  const [storedContext, setStoredContext] = useState<OrbitEntryContext | null>(null);
  /**
   * What the session is about, for the launcher's label card. Hydrated from storage because the
   * launcher is mounted only while the panel is CLOSED, and the transcript this is derived from is
   * fetched only while it is open.
   */
  const [sessionSubject, setSessionSubject] = useState<string | undefined>(undefined);
  const [contextPinned, setContextPinned] = useState(false);
  const [dismissedContextKey, setDismissedContextKey] = useState<string | null>(null);
  /** Filters the session was started under: their identity, and the summary the compare row shows. */
  const [sessionFilterKey, setSessionFilterKey] = useState<string | null>(null);
  const [sessionFilters, setSessionFilters] = useState<string>('');
  /**
   * The cards that were on screen when the session was started, as one joined identity, so a later
   * scroll to a different screenful is a change (PLA-8844).
   */
  const [sessionCardsKey, setSessionCardsKey] = useState<string | null>(null);
  /**
   * The drawn form of the two above: the chips and the card titles as the screen printed them when
   * the session was started. The context row falls back to these once the conversation is PINNED,
   * because from then on they - not the live screen - are what the next question is sent under
   * (PLA-9563 §1c).
   */
  const [sessionChips, setSessionChips] = useState<ScreenFilterChip[]>(NO_CHIPS);
  const [sessionCards, setSessionCards] = useState<ScreenCardInView[]>(NO_CARDS);
  /**
   * Whether a context change is waiting for the user's decision. LATCHED, not derived from the live
   * comparison: the card in view follows the scroll position, so a derived card vanished the moment
   * the reader scrolled back past the card the conversation started on and came back on the way
   * down (user, 2026-09-14). Once raised it stays until one of the card's three actions answers it.
   */
  const [conflictRaised, setConflictRaised] = useState(false);
  /** True while the page is still reflowing around a panel toggle; see ORBIT_PANEL_RELAYOUT_MS. */
  const [panelRelayout, setPanelRelayout] = useState(false);
  const [handoffContext, setHandoffContext] = useState<OrbitEntryContext | null>(null);
  const [handoffAfterTurnId, setHandoffAfterTurnId] = useState<number | null>(null);

  /**
   * Bumped by every action that abandons the conversation an in-flight `ask()` belongs to (a second
   * ask, New Chat, resuming another conversation, clearing). The awaited POST compares the
   * generation it started in before writing any state, so a late response cannot resurrect a
   * conversation the user has already left or overwrite a newer turn.
   */
  const generationRef = useRef(0);
  /**
   * The last send that got no response. The next send from the same conversation goes to the
   * session it went to, so a new chat keeps the id it minted, and a resend of the same question
   * first looks for the turn it may have created. Cleared by a send that succeeds and by every
   * action that abandons the conversation.
   */
  const unconfirmedSendRef = useRef<UnconfirmedOrbitSend | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  /*
    Read by the relayout timer below, which must see the values current when it FIRES rather than
    the ones its effect closed over, and must not restart every time the detector republishes.
  */
  const entryCardsKeyRef = useRef(entryCardsKey);
  const entryCardsRef = useRef(entryCards);
  const storageKeyRef = useRef(storageKey);
  const conflictRaisedRef = useRef(conflictRaised);
  entryCardsKeyRef.current = entryCardsKey;
  entryCardsRef.current = entryCards;
  storageKeyRef.current = storageKey;
  conflictRaisedRef.current = conflictRaised;

  useEffect(() => {
    generationRef.current += 1;
    unconfirmedSendRef.current = null;
    const stored = readStoredOrbitSession(storageKey);
    setSessionId(stored?.id ?? null);
    setStoredContext(stored?.context ?? null);
    setContextPinned(stored?.pinned === true);
    setSessionSubject(stored?.subject);
    // Hydrated, not reset: a decision already taken about this exact context must survive the
    // reload that re-reads it, or the card re-asks it on every refresh (PLA-8844 D3).
    setDismissedContextKey(stored?.dismissedContextKey ?? null);
    setSessionFilterKey(stored?.filterKey ?? null);
    setSessionFilters(stored?.filterLabel ?? '');
    setSessionCardsKey(stored?.cardKeys?.join(CARD_KEY_SEPARATOR) ?? null);
    setSessionChips(stored?.filterChips ?? NO_CHIPS);
    setSessionCards(stored?.cards ?? NO_CARDS);
    setConflictRaised(false);
    setHandoffContext(null);
    setHandoffAfterTurnId(null);
    setActiveTurnId(null);
    setPendingQuestion(null);
  }, [storageKey]);

  const {
    turns: listedTurns,
    hasMore: hasOlderTurns,
    hasLoaded: turnsLoaded,
    isLoading: turnsLoading,
    isLoadingMore: turnsLoadingMore,
    error: turnsError,
    loadMore: loadOlderTurns,
    mutate: revalidateTurns
  } = useOrbitTurnPages(transcriptActive && sessionId !== null ? { session_id: sessionId } : null);

  /** Turns whose poll answered 403/404: no answer will ever come for them. */
  const [abandonedTurnIds, setAbandonedTurnIds] = useState<ReadonlySet<number>>(NO_TURN_IDS);
  /** Turns whose poll has reached a terminal state. */
  const [settledTurnIds, setSettledTurnIds] = useState<ReadonlySet<number>>(NO_TURN_IDS);

  // The list can still report an abandoned turn in flight; it is drawn as failed instead. When it
  // reports none at all, the locally kept copy stands in, so a question whose turn the server has
  // forgotten still answers for itself instead of disappearing.
  const reachableTurns = useMemo(() => {
    const marked =
      abandonedTurnIds.size === 0
        ? listedTurns
        : listedTurns.map(entry =>
            abandonedTurnIds.has(entry.id) && !isTerminalOrbitState(entry.state)
              ? { ...entry, state: 'failed' as const }
              : entry
          );

    if (lostTurns.size === 0) {
      return marked;
    }

    const listed = new Set(marked.map(entry => entry.id));
    const missing = Array.from(lostTurns.values()).filter(entry => !listed.has(entry.id));

    return missing.length === 0 ? marked : [...marked, ...missing];
  }, [listedTurns, abandonedTurnIds, lostTurns]);
  // A turn pending for too long is drawn as timed out, which also keeps it from being polled.
  const pendingSinceRef = useRef<OrbitPendingSince>(new Map());
  const turns = useOrbitTurnTimeouts(reachableTurns, pendingSinceRef.current);

  /**
   * The one turn this tab polls. The turn it just asked comes first until its poll settles; then,
   * as after a reload or a resumed conversation, the newest turn the transcript still reports in
   * flight, which also covers a turn asked from another tab. The asked turn stays the fallback so
   * its settled copy is still drawn while the list catches up.
   */
  const inFlightTurnId =
    activeTurnId !== null && !settledTurnIds.has(activeTurnId) ? activeTurnId : null;
  const pollTurnId =
    inFlightTurnId ?? turns.find(entry => !isTerminalOrbitState(entry.state))?.id ?? activeTurnId;

  const {
    turn: activeTurn,
    error: activeTurnError,
    mutate: revalidateActiveTurn
  } = useOrbitTurn(pollTurnId, pendingSinceRef.current);

  /**
   * The transcript's Retry. A failed poll is restarted with it, from a fresh retry count, because a
   * poll that used up its retries is not restarted by refetching the list alone.
   */
  const reloadTranscript = useCallback<SWRInfiniteKeyedMutator<OrbitListResponse[]>>(
    // Forwarded as given: SWR tells a revalidation from a cache write by the argument count.
    (...args) => {
      if (activeTurnError !== undefined) {
        revalidateActiveTurn();
      }

      return revalidateTurns(...args);
    },
    [activeTurnError, revalidateActiveTurn, revalidateTurns]
  );

  const activeTurnState = activeTurn?.state;
  const activeTurnErrorStatus: number | undefined = (activeTurnError as any)?.response?.status;
  const activeTurnErrorFatal =
    activeTurnError !== undefined && isFatalOrbitPollError(activeTurnError);

  useEffect(() => {
    if (pollTurnId === null || !isTerminalOrbitState(activeTurnState)) {
      return;
    }

    setSettledTurnIds(current => withTurnId(current, pollTurnId));
    if (pollTurnId === activeTurnId) {
      setPendingQuestion(null);
    }
    revalidateTurns();
    // An answer can list a new chat, or move one to the top of Recent.
    revalidateOrbitSessionLists();
  }, [pollTurnId, activeTurnId, activeTurnState, revalidateTurns]);

  useEffect(() => {
    if (pollTurnId === null || activeTurnErrorStatus === undefined) {
      return;
    }

    // Unmounting the poll is what stops it, and leaves the transcript to draw the turn as failed.
    if (activeTurnErrorFatal) {
      setAbandonedTurnIds(current => withTurnId(current, pollTurnId));
      // Usually the turn exists server-side and the refetched list brings the question back. When
      // the server has forgotten it too, this copy is the only record that it was ever asked, and
      // `pendingQuestion` is about to be cleared, so it is taken before that happens.
      if (pollTurnId === activeTurnId && pendingQuestion !== null) {
        const kept = lostTurn(pollTurnId, sessionId, pendingQuestion);

        setLostTurns(current =>
          current.has(pollTurnId) ? current : new Map(current).set(pollTurnId, kept)
        );
      }
      setActiveTurnId(current => (current === pollTurnId ? null : current));
    }
    if (pollTurnId === activeTurnId) {
      setPendingQuestion(null);
    }
    revalidateTurns();
    // The question and the session id are read above, so they belong here. Re-running is harmless:
    // a kept turn is `failed`, which is terminal, so nothing is left to poll and the guard returns.
  }, [
    pollTurnId,
    activeTurnId,
    activeTurnErrorStatus,
    activeTurnErrorFatal,
    pendingQuestion,
    sessionId,
    revalidateTurns
  ]);

  useOrbitWorkspaceFiling(sessionId, turns, activeTurn);

  const dropSession = useCallback(() => {
    generationRef.current += 1;
    unconfirmedSendRef.current = null;
    setSessionId(null);
    setActiveTurnId(null);
    setPendingQuestion(null);
    setLostTurns(NO_LOST_TURNS);
    setStoredContext(null);
    setContextPinned(false);
    setDismissedContextKey(null);
    setSessionFilterKey(null);
    setSessionFilters('');
    setSessionCardsKey(null);
    setSessionChips(NO_CHIPS);
    setSessionCards(NO_CARDS);
    setConflictRaised(false);
    setHandoffContext(null);
    setHandoffAfterTurnId(null);
    setSessionSubject(undefined);
    // The composer belongs to the investigation being dropped: a half-typed question left in it is
    // the previous investigation continuing into the new one (PLA-9563 §1a).
    setDraft('');
    clearStoredOrbitSession(storageKey);
  }, [storageKey]);

  /**
   * The context the running investigation belongs to. It is whatever was persisted for the session,
   * falling back to the oldest loaded turn so a conversation resumed from the list — which arrives
   * as an id and nothing else — still has something to compare the live route against.
   */
  const sessionContext =
    storedContext ?? entryContextOf(turns.length > 0 ? turns[turns.length - 1] : undefined);

  /**
   * Captures what the session is about the moment the transcript makes it knowable. The launcher
   * names the running investigation while the panel is CLOSED, and `useOrbitTurnPages` above is
   * keyed only while it is open, so the value is persisted beside the session id exactly as the
   * session's context is — storage is the launcher's only route to session state.
   */
  useEffect(() => {
    if (sessionId === null) {
      return;
    }

    const subject = orbitSessionSubject(turns);

    if (subject === undefined || subject === sessionSubject) {
      return;
    }

    setSessionSubject(subject);
    updateStoredOrbitSession(storageKey, { subject });
  }, [sessionId, turns, sessionSubject, storageKey]);

  const routeChanged =
    entryContext.screen !== sessionContext?.screen ||
    (entryContext.screen === ORBIT_SCREEN_CUSTOMER_360 &&
      entryContext.customer_id !== sessionContext?.customer_id);

  /**
   * Baseline for a conversation whose filter signature was never recorded: one reopened from the
   * list arrives as an id alone (`resumeSession` below), and one stored before filters joined the
   * context carries no `filterKey` at all. Without this the filter half of the rule below is dead
   * for the rest of that conversation's life and only a route change could ever raise the card
   * (PLA-8844). Adopted only once the transcript proves the conversation belongs to the screen the
   * user is on, so arriving with a filter already applied is not itself a change — a LATER one is.
   */
  useEffect(() => {
    if (
      sessionId === null ||
      turns.length === 0 ||
      sessionFilterKey !== null ||
      entryFilterKey === null ||
      routeChanged
    ) {
      return;
    }

    setSessionFilterKey(entryFilterKey);
    setSessionFilters(entryFilterLabel);
    setSessionChips(entryChips);
    updateStoredOrbitSession(storageKey, {
      filterKey: entryFilterKey,
      filterLabel: entryFilterLabel,
      filterChips: entryChips
    });
  }, [
    sessionId,
    turns.length,
    sessionFilterKey,
    entryFilterKey,
    entryFilterLabel,
    entryChips,
    routeChanged,
    storageKey
  ]);

  /**
   * The same baseline, for the visible set. It is separate from the filters' because the two arrive
   * at different times: the filters are published on the screen's first render and the set only once
   * the observer has settled, so sharing one baseline would make the set's arrival read as a filter
   * change on every page load with a conversation already running.
   */
  useEffect(() => {
    if (
      sessionId === null ||
      turns.length === 0 ||
      sessionCardsKey !== null ||
      entryCardsKey === null ||
      routeChanged
    ) {
      return;
    }

    setSessionCardsKey(entryCardsKey);
    setSessionCards(entryCards);
    updateStoredOrbitSession(storageKey, {
      cardKeys: storedCardKeys(entryCardsKey),
      cards: entryCards
    });
  }, [
    sessionId,
    turns.length,
    sessionCardsKey,
    entryCardsKey,
    entryCards,
    routeChanged,
    storageKey
  ]);

  /**
   * A filter or card change is a context change: the same screen now answers about a different
   * population, or about a different question, so one asked after it means something the running
   * investigation did not (FR §4.10 Scenario 4). Any change to the signature is material - the date
   * range included, since it is built from every category the screen exposes, and the cards on
   * screen, which the reader reaches by scrolling rather than by clicking. The set is material as a
   * SET: any card entering or leaving it changes what the screen is answering about. Only a change
   * BETWEEN two known signatures counts — the effect above supplies the missing baseline, and leaving
   * the screen that publishes them retracts the signature, which is the route's change to report and
   * not a second one on top of it. The card half is settled before it is published
   * (`useCardInView`), so one scroll gesture through three screenfuls raises the card once and for
   * the set it stopped on.
   */
  const filtersChanged =
    sessionFilterKey !== null && entryFilterKey !== null && entryFilterKey !== sessionFilterKey;
  const cardChanged =
    !panelRelayout &&
    sessionCardsKey !== null &&
    entryCardsKey !== null &&
    entryCardsKey !== sessionCardsKey;

  /**
   * Opening or closing the panel is not a context change, so the comparison is held while the page
   * reflows around it and the set it comes to rest on is adopted as the new baseline. Only the
   * CARDS are re-anchored: a route or a filter the user changed while the panel was closed is still
   * their own change and raises the card as soon as the window closes (PLA-8844).
   *
   * The baseline is never CREATED here, only moved: an absent one is the other effects' to supply.
   */
  useEffect(() => {
    setPanelRelayout(true);

    const timer = setTimeout(() => {
      const settled = entryCardsKeyRef.current;

      setPanelRelayout(false);

      if (settled === null || conflictRaisedRef.current) {
        return;
      }

      setSessionCardsKey(current => (current === null ? current : settled));
      setSessionCards(entryCardsRef.current);
      updateStoredOrbitSession(storageKeyRef.current, {
        cardKeys: storedCardKeys(settled),
        cards: entryCardsRef.current
      });
    }, ORBIT_PANEL_RELAYOUT_MS);

    return () => clearTimeout(timer);
  }, [open]);

  const contextChanged =
    sessionId !== null &&
    turns.length > 0 &&
    sessionContext !== null &&
    // A route Orbit does not recognise resolves to `{}`; treating that as a change would raise the
    // card on every unrelated page in the dashboard. Only the LIVE side is tested: a conversation
    // opened from a module Orbit has no context for is stored with `{}`, and requiring a screen on
    // the session side too left the rule dead for the rest of that conversation - the panel was
    // carried from Campaigns onto Customer Insights and never reported the context it had just
    // acquired (user, 2026-09-23; browser recheck 28 rows 6-7).
    entryContext.screen !== undefined &&
    (routeChanged || filtersChanged || cardChanged);

  const contextChangePending =
    ORBIT_CONTEXT_DETECTION_ENABLED &&
    contextChanged &&
    dismissedContextKey !== contextKey(entryContext, entryFilterKey, entryCardsKey);

  /**
   * Raising is one-way. The card interrupts the panel with a decision to take, so a later scroll
   * that happens to restore the comparison - back past the card the conversation started on, then
   * forward again - must not withdraw the question and re-ask it (user, 2026-09-14). What the live
   * side of the compare row says keeps following the screen while it is up, because the card reads
   * `entryContext` and `entryFilters` directly.
   *
   * The one thing that lowers it without a decision is the live route ceasing to be one Orbit
   * recognises: the compare row would then have no screen to name.
   */
  useEffect(() => {
    if (contextChangePending) {
      setConflictRaised(true);
      return;
    }

    if (entryContext.screen === undefined) {
      setConflictRaised(false);
    }
  }, [contextChangePending, entryContext.screen]);

  const contextConflict = conflictRaised;

  /**
   * Every question this conversation has already put to Orbit, in comparable form. The suggested
   * action rows under an answer are drawn from the same static catalogue the idle panel offers, so
   * without this the answer to "Which segments need attention?" ended by offering that same question
   * again - after every answer, for the whole visit (PLA-9563 §2b).
   */
  const askedQuestions = useMemo(() => {
    const keys = new Set<string>();

    turns.forEach(turn => keys.add(orbitQuestionKey(turn.question)));

    if (pendingQuestion !== null) {
      keys.add(orbitQuestionKey(pendingQuestion));
    }

    return keys;
  }, [turns, pendingQuestion]);

  /**
   * A cleared or expired conversation is NOT an error status: the list endpoint answers `200` with
   * `data: []` for an unknown `session_id`, and its only error is the gate-level `403`
   * (contract §7). So the stale-session signal is a transcript that resolved empty for a stored id
   * — guarded on there being no turn of our own in flight, which would legitimately outrun the list.
   */
  useEffect(() => {
    if (
      sessionId === null ||
      !turnsLoaded ||
      turns.length > 0 ||
      activeTurnId !== null ||
      pendingQuestion !== null
    ) {
      return;
    }

    setSessionStale(true);
    dropSession();
  }, [sessionId, turnsLoaded, turns.length, activeTurnId, pendingQuestion, dropSession]);

  const rememberOpener = () => {
    openerRef.current = document.activeElement as HTMLElement | null;
  };

  const openSidebar = useCallback(() => {
    rememberOpener();
    setOpen(true);
  }, []);
  const closeSidebar = useCallback(() => setOpen(false), []);
  const toggleSidebar = useCallback(() => {
    if (!open) {
      rememberOpener();
    }
    setOpen(!open);
  }, [open]);
  const restoreOpenerFocus = useCallback(() => {
    openerRef.current?.focus?.({ preventScroll: true });
    openerRef.current = null;
  }, []);

  /**
   * Another tab asking in this conversation is invisible here until the transcript is refetched, so
   * its storage write and this tab coming back into view both refetch it; a turn still in flight is
   * then polled from the list. A hidden tab leaves the write to its own return into view. A write
   * that points the stored session at another conversation is not followed, so the thread the
   * reader has open here stays put.
   */
  useEffect(() => {
    if (!transcriptActive || sessionId === null) {
      return undefined;
    }

    let refreshedAt = 0;
    const refresh = () => {
      const now = Date.now();

      if (now - refreshedAt < ORBIT_TRANSCRIPT_REFRESH_THROTTLE_MS) {
        return;
      }

      refreshedAt = now;
      revalidateTurns();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === storageKey &&
        document.visibilityState !== 'hidden' &&
        readStoredOrbitSession(storageKey)?.id === sessionId
      ) {
        refresh();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('storage', onStorage);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [transcriptActive, sessionId, storageKey, revalidateTurns]);

  /**
   * The conversation list reads the UNSCOPED feed through `useSWRInfinite`, whose keys always carry
   * `page=`/`size=` and so never collide with the session-scoped transcript. One matcher covers
   * every Orbit list view — plain and infinite — so a cleared or newly created conversation cannot
   * be left on screen (`data-fetching.md` Critical Rule 4). It matches list keys only: a polled
   * turn's own key shares the path, and re-reading a settled turn is a wasted request.
   */
  const revalidateOrbitLists = useCallback(
    () => mutateCache(key => typeof key === 'string' && key.startsWith(`${ORBIT_REQUESTS_PATH}?`)),
    [mutateCache]
  );

  const ask = useCallback(
    async (question: string): Promise<OrbitMutationOutcome<OrbitTurn>> => {
      const trimmed = question.trim();
      generationRef.current += 1;
      const generation = generationRef.current;
      // Keep Current Investigation pins the investigation's own context; every other state sends the
      // route the user is actually looking at. With detection off a stored pin can be neither seen
      // nor undone, so the route wins.
      const askContext =
        ORBIT_CONTEXT_DETECTION_ENABLED && contextPinned && sessionContext !== null
          ? sessionContext
          : entryContext;
      const startsSession = sessionId === null;
      const unconfirmed =
        unconfirmedSendRef.current?.fromSessionId === sessionId ? unconfirmedSendRef.current : null;
      // A new chat names its own session, so a POST that gets no response can still be looked up.
      const sendSessionId = sessionId ?? unconfirmed?.sessionId ?? uuid();
      const resend = unconfirmed !== null && unconfirmed.question === trimmed;
      const knownTurnIds = resend ? unconfirmed.knownTurnIds : turns.map(known => known.id);
      const lostResponse: OrbitMutationFailure = { kind: 'network', message: null };

      setPendingQuestion(trimmed);
      setAskError(null);
      setSessionStale(false);

      // The turn an unanswered POST created after all, `null` when it created none.
      const findLanded = async (): Promise<OrbitMutationOutcome<OrbitTurn> | null> => {
        const landed = await findLandedOrbitTurn(sendSessionId, trimmed, knownTurnIds);

        if (landed.kind !== 'ok') {
          return lostResponse;
        }

        return landed.data === null ? null : { kind: 'ok', data: landed.data };
      };

      let outcome = resend ? await findLanded() : null;

      if (outcome === null) {
        // Left while the session was being read back: nothing is posted into an abandoned one.
        if (generationRef.current !== generation) {
          return lostResponse;
        }

        outcome = await createOrbitRequest({
          question: trimmed,
          session_id: sendSessionId,
          context: toOrbitWireContext(askContext)
        });

        if (outcome.kind === 'network' && generationRef.current === generation) {
          outcome = (await findLanded()) ?? outcome;
        }
      }

      if (generationRef.current !== generation) {
        return outcome;
      }

      if (outcome.kind === 'ok') {
        unconfirmedSendRef.current = null;
        // An adopted turn is timed from now, not from whenever its first poll answers.
        markOrbitTurnSeen(pendingSinceRef.current, outcome.data);
        setSessionId(outcome.data.session_id);
        setActiveTurnId(outcome.data.id);

        if (startsSession) {
          setStoredContext(askContext);
          setSessionFilterKey(entryFilterKey);
          setSessionFilters(entryFilterLabel);
          setSessionCardsKey(entryCardsKey);
          setSessionChips(entryChips);
          setSessionCards(entryCards);
        }

        writeStoredOrbitSession(storageKey, {
          id: outcome.data.session_id,
          context: toOrbitWireContext((startsSession ? askContext : sessionContext) ?? {}),
          pinned: contextPinned,
          // A new conversation is about something else; the capture effect above re-derives it from
          // the first turn. Continuing one keeps the subject it already had.
          subject: startsSession ? undefined : sessionSubject,
          filterKey: (startsSession ? entryFilterKey : sessionFilterKey) ?? undefined,
          filterLabel: startsSession ? entryFilterLabel : sessionFilters,
          cardKeys: storedCardKeys(startsSession ? entryCardsKey : sessionCardsKey),
          filterChips: startsSession ? entryChips : sessionChips,
          cards: startsSession ? entryCards : sessionCards,
          dismissedContextKey: startsSession ? undefined : dismissedContextKey ?? undefined,
          latestTurnId: outcome.data.id
        });
        revalidateOrbitLists();
      } else {
        if (outcome.kind === 'network') {
          unconfirmedSendRef.current = {
            fromSessionId: sessionId,
            sessionId: sendSessionId,
            question: trimmed,
            knownTurnIds
          };
        }

        setPendingQuestion(null);
        setAskError(outcome);
      }

      return outcome;
    },
    [
      sessionId,
      turns,
      entryContext,
      sessionContext,
      contextPinned,
      sessionSubject,
      sessionFilterKey,
      sessionFilters,
      sessionCardsKey,
      sessionChips,
      sessionCards,
      entryFilterKey,
      entryFilterLabel,
      entryCardsKey,
      entryChips,
      entryCards,
      dismissedContextKey,
      storageKey,
      revalidateOrbitLists
    ]
  );

  /** Continue with New Context: the live route and its filters become the investigation's own. */
  const adoptLiveContext = useCallback(() => {
    setStoredContext(entryContext);
    setContextPinned(false);
    setDismissedContextKey(null);
    setConflictRaised(false);
    setSessionFilterKey(entryFilterKey);
    setSessionFilters(entryFilterLabel);
    setSessionCardsKey(entryCardsKey);
    setSessionChips(entryChips);
    setSessionCards(entryCards);
    // The transcript records the move: the screen changed, the investigation did not. The card is
    // anchored to the turn it followed, so questions asked afterwards land BELOW it.
    setHandoffContext(entryContext);
    setHandoffAfterTurnId(turns.length > 0 ? turns[0].id : null);

    if (sessionId !== null) {
      writeStoredOrbitSession(storageKey, {
        id: sessionId,
        context: toOrbitWireContext(entryContext),
        pinned: false,
        subject: sessionSubject,
        filterKey: entryFilterKey ?? undefined,
        filterLabel: entryFilterLabel,
        cardKeys: storedCardKeys(entryCardsKey),
        filterChips: entryChips,
        cards: entryCards
      });
    }
  }, [
    entryContext,
    entryFilterKey,
    entryFilterLabel,
    entryCardsKey,
    entryChips,
    entryCards,
    sessionId,
    sessionSubject,
    storageKey,
    turns
  ]);

  /** Keep Current Context: freeze the session's own context and stop offering the swap here. */
  const pinSessionContext = useCallback(() => {
    const dismissed = contextKey(entryContext, entryFilterKey, entryCardsKey);

    setStoredContext(sessionContext);
    setContextPinned(true);
    setDismissedContextKey(dismissed);
    setConflictRaised(false);

    if (sessionId !== null && sessionContext !== null) {
      writeStoredOrbitSession(storageKey, {
        id: sessionId,
        context: toOrbitWireContext(sessionContext),
        pinned: true,
        subject: sessionSubject,
        filterKey: sessionFilterKey ?? undefined,
        filterLabel: sessionFilters,
        cardKeys: storedCardKeys(sessionCardsKey),
        filterChips: sessionChips,
        cards: sessionCards,
        // Persisted with the pin: the pin alone survived a reload while the decision did not, so the
        // card was raised again for a context the user had already answered about (PLA-8844 D3).
        dismissedContextKey: dismissed
      });
    }
  }, [
    sessionContext,
    entryContext,
    entryFilterKey,
    entryCardsKey,
    sessionId,
    sessionSubject,
    sessionFilterKey,
    sessionFilters,
    sessionCardsKey,
    sessionChips,
    sessionCards,
    storageKey
  ]);

  const newChat = useCallback(() => {
    setAskError(null);
    setSessionStale(false);
    // Withheld when there is nothing to confirm: no session, or one whose opening turn never
    // yielded a subject to name.
    if (sessionId !== null && sessionSubject !== undefined) {
      showNotice(orbitNewInvestigationNotice(sessionSubject));
    }

    dropSession();
  }, [dropSession, sessionId, sessionSubject, showNotice]);

  const resumeSession = useCallback(
    (nextSessionId: string) => {
      generationRef.current += 1;
      unconfirmedSendRef.current = null;
      setAskError(null);
      setSessionStale(false);
      setActiveTurnId(null);
      setPendingQuestion(null);
      setSessionId(nextSessionId);
      // The list hands over an id and nothing else; the resumed session's context is re-derived
      // from its oldest loaded turn.
      setStoredContext(null);
      setContextPinned(false);
      setDismissedContextKey(null);
      setSessionFilterKey(null);
      setSessionFilters('');
      setSessionCardsKey(null);
      setSessionChips(NO_CHIPS);
      setSessionCards(NO_CARDS);
      setConflictRaised(false);
      setHandoffContext(null);
      setHandoffAfterTurnId(null);
      // A different conversation is about something else; the effect below re-derives it once this
      // session's transcript loads.
      setSessionSubject(undefined);
      writeStoredOrbitSession(storageKey, { id: nextSessionId });
    },
    [storageKey]
  );

  const clearConversation = useCallback(async (): Promise<OrbitMutationOutcome<null>> => {
    if (sessionId === null) {
      return { kind: 'ok', data: null };
    }

    const outcome = await clearOrbitSession(sessionId);

    if (outcome.kind === 'ok') {
      setAskError(null);
      setSessionStale(false);
      dropSession();
      revalidateOrbitLists();
    }

    return outcome;
  }, [sessionId, dropSession, revalidateOrbitLists]);

  const value = useMemo<OrbitContextValue>(
    () => ({
      open,
      attachPageSurface,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      restoreOpenerFocus,
      sessionId,
      activeTurnId,
      activeTurn,
      activeTurnError,
      pendingQuestion,
      isAsking: pendingQuestion !== null,
      askError,
      draft,
      setDraft,
      entryContext,
      sessionContext,
      sessionSubject,
      entryFilters,
      sessionFilters,
      sessionChips,
      sessionCards,
      contextPinned,
      askedQuestions,
      contextConflict,
      handoffContext,
      handoffAfterTurnId,
      adoptLiveContext,
      pinSessionContext,
      turns,
      turnsLoading,
      turnsLoadingMore,
      hasOlderTurns,
      loadOlderTurns,
      turnsError,
      sessionStale,
      ask,
      newChat,
      resumeSession,
      clearConversation,
      revalidateTurns: reloadTranscript
    }),
    [
      open,
      attachPageSurface,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      restoreOpenerFocus,
      sessionId,
      activeTurnId,
      activeTurn,
      activeTurnError,
      pendingQuestion,
      askError,
      draft,
      entryContext,
      sessionContext,
      sessionSubject,
      entryFilters,
      sessionFilters,
      sessionChips,
      sessionCards,
      contextPinned,
      askedQuestions,
      contextConflict,
      handoffContext,
      handoffAfterTurnId,
      adoptLiveContext,
      pinSessionContext,
      turns,
      turnsLoading,
      turnsLoadingMore,
      hasOlderTurns,
      loadOlderTurns,
      turnsError,
      sessionStale,
      ask,
      newChat,
      resumeSession,
      clearConversation,
      reloadTranscript
    ]
  );

  return (
    <OrbitContext.Provider value={value}>
      <OrbitNoticeContext.Provider value={notices}>
        {children}
        <OrbitNoticeHost />
      </OrbitNoticeContext.Provider>
    </OrbitContext.Provider>
  );
};

export default OrbitProvider;
